import { useState, useEffect, useRef } from 'react'

const TYPES = [
  { id: 'bug',     label: '🐛 Bug',  hint: 'Something broken' },
  { id: 'feature', label: '✨ Idea', hint: 'A feature you wish Flow had' },
  { id: 'other',   label: '💬 Other', hint: 'Anything on your mind' },
]

const COOLDOWN_MS = 5 * 60 * 1000
const LS_KEY = 'flow_feedback_sent_at'

function msToClock(ms) {
  const totalSec = Math.ceil(ms / 1000)
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`
}

export default function SettingsFeedback({ bgMode }) {
  const [type, setType]       = useState('bug')
  const [message, setMsg]     = useState('')
  const [phase, setPhase]     = useState('idle') // idle | sending | done | error | cooldown
  const [errMsg, setErrMsg]   = useState('')
  const [remaining, setRemaining] = useState(0)
  const intervalRef = useRef(null)

  useEffect(() => {
    function tick() {
      const sentAt = parseInt(localStorage.getItem(LS_KEY) || '0', 10)
      const elapsed = Date.now() - sentAt
      const left = COOLDOWN_MS - elapsed
      if (left > 0) {
        setRemaining(left)
        setPhase(prev => prev === 'idle' || prev === 'cooldown' ? 'cooldown' : prev)
      } else {
        setRemaining(0)
        setPhase(prev => prev === 'cooldown' ? 'idle' : prev)
      }
    }
    tick()
    intervalRef.current = setInterval(tick, 1000)
    return () => clearInterval(intervalRef.current)
  }, [])

  const submit = async () => {
    if (message.trim().length < 5 || phase === 'sending') return
    setPhase('sending')
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, message: message.trim(), page: window.location.pathname }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to send')
      localStorage.setItem(LS_KEY, Date.now().toString())
      setPhase('done')
      setMsg('')
    } catch (e) {
      setErrMsg(e.message)
      setPhase('error')
    }
  }

  const dark = bgMode === 'dark'

  const panel = {
    background: dark ? 'rgba(4,14,7,0.82)' : 'rgba(255,255,255,0.75)',
    border: dark ? '1.5px solid rgba(34,197,94,0.14)' : '1.5px solid rgba(34,197,94,0.22)',
    borderRadius: 16, padding: 18, marginTop: 16, backdropFilter: 'blur(12px)',
  }
  const sectionLabel = {
    display: 'block', fontSize: 11, fontWeight: 700,
    color: dark ? 'rgba(180,215,235,0.65)' : 'rgba(30,50,40,0.65)',
    textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14,
  }

  return (
    <div style={panel}>
      <label style={sectionLabel}>Help Us Improve</label>

      {phase === 'done' && (
        <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>🙏</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: dark ? '#c8e6cb' : '#15803d', marginBottom: 6 }}>
            Thanks — we read everything.
          </div>
          <div style={{ fontSize: 12, color: dark ? 'rgba(140,200,150,0.5)' : 'rgba(30,80,40,0.45)', lineHeight: 1.65 }}>
            You can send more feedback in 5 minutes.
          </div>
        </div>
      )}

      {phase === 'cooldown' && (
        <div style={{ textAlign: 'center', padding: '10px 0 4px' }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>⏳</div>
          <div style={{ fontSize: 13, color: dark ? 'rgba(140,200,150,0.7)' : 'rgba(30,80,40,0.6)', lineHeight: 1.7 }}>
            Thanks for your message. Next submission in{' '}
            <span style={{ fontWeight: 800, color: dark ? '#86efac' : '#15803d' }}>
              {msToClock(remaining)}
            </span>
          </div>
        </div>
      )}

      {(phase === 'idle' || phase === 'sending' || phase === 'error') && (
        <>
          {/* Type pills */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {TYPES.map(t => {
              const sel = type === t.id
              return (
                <button
                  key={t.id}
                  onClick={() => setType(t.id)}
                  title={t.hint}
                  style={{
                    flex: 1,
                    border: `1.5px solid ${sel ? 'rgba(34,197,94,0.4)' : (dark ? 'rgba(255,255,255,0.07)' : 'rgba(34,197,94,0.18)')}`,
                    background: sel
                      ? (dark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.1)')
                      : (dark ? 'rgba(3,10,4,0.5)' : 'rgba(0,0,0,0.04)'),
                    borderRadius: 10, padding: '9px 4px',
                    color: sel
                      ? (dark ? '#86efac' : '#15803d')
                      : (dark ? 'rgba(140,200,150,0.5)' : 'rgba(30,80,40,0.4)'),
                    fontSize: 11, fontWeight: 700, cursor: 'pointer',
                    fontFamily: 'inherit', transition: 'all 0.15s',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {t.label}
                </button>
              )
            })}
          </div>

          {/* Textarea */}
          <textarea
            value={message}
            onChange={e => { setMsg(e.target.value); if (phase === 'error') setPhase('idle') }}
            placeholder="Tell us what's on your mind…"
            rows={3}
            maxLength={2000}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: dark ? 'rgba(4,14,7,0.85)' : 'rgba(255,255,255,0.9)',
              border: `1.5px solid ${phase === 'error' ? 'rgba(248,113,113,0.4)' : (dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.25)')}`,
              borderRadius: 11, padding: '10px 12px',
              color: dark ? '#c8e6cb' : '#1e2922',
              fontSize: 13, lineHeight: 1.6,
              fontFamily: 'inherit', resize: 'none', outline: 'none',
              caretColor: '#22c55e', transition: 'border-color 0.15s',
            }}
            onFocus={e => { e.target.style.borderColor = 'rgba(34,197,94,0.4)' }}
            onBlur={e => { e.target.style.borderColor = phase === 'error' ? 'rgba(248,113,113,0.4)' : (dark ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.25)') }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 5, marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: '#fca5a5', minHeight: 14 }}>
              {phase === 'error' ? errMsg : ''}
            </div>
            <div style={{ fontSize: 10, color: dark ? 'rgba(140,200,150,0.3)' : 'rgba(30,80,40,0.3)' }}>
              {message.length} / 2000
            </div>
          </div>

          <button
            onClick={submit}
            disabled={message.trim().length < 5 || phase === 'sending'}
            style={{
              width: '100%', borderRadius: 11,
              padding: '11px 0', fontSize: 13, fontWeight: 700,
              fontFamily: 'inherit', transition: 'all 0.18s',
              cursor: message.trim().length >= 5 && phase !== 'sending' ? 'pointer' : 'default',
              background: message.trim().length >= 5
                ? 'linear-gradient(135deg,rgba(34,197,94,0.28),rgba(20,120,50,0.2))'
                : (dark ? 'rgba(4,14,7,0.6)' : 'rgba(0,0,0,0.05)'),
              border: `1.5px solid ${message.trim().length >= 5 ? 'rgba(34,197,94,0.4)' : (dark ? 'rgba(255,255,255,0.05)' : 'rgba(34,197,94,0.1)')}`,
              color: message.trim().length >= 5
                ? (dark ? '#86efac' : '#15803d')
                : (dark ? 'rgba(140,200,150,0.25)' : 'rgba(30,80,40,0.2)'),
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {phase === 'sending' ? 'Sending…' : 'Send feedback'}
          </button>
        </>
      )}
    </div>
  )
}
